import { BasicText, Loader, spacing, TextBadge } from '@scality/core-ui';
import { Box } from '@scality/core-ui/dist/next';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { BackgroundTasksContext, type UiTask } from '../../hooks/useBackgroundTasks';

// Status → core-ui TextBadge variant (theme-driven colors, no hardcoded hex).
const BADGE_VARIANT = {
  running: 'statusWarning',
  succeeded: 'statusHealthy',
  failed: 'statusCritical',
} as const satisfies Record<UiTask['status'], string>;

const LABEL: Record<UiTask['status'], string> = {
  running: 'Working',
  succeeded: 'Done',
  failed: 'Failed',
};

// How many tasks stay visible before the list scrolls, and the height of one row.
const VISIBLE_ROWS = 3;
const ROW_HEIGHT = spacing.r24;

// Sits above the prompt inside the composer; only rendered when ≥ 1 task is active.
// Padding uses core-ui spacing tokens: bottom keeps it off the prompt, right keeps
// the badge clear of the screen edge. The padding stays OUT of the scroll area below,
// so the scrollbar can't land between the badge and the edge.
//
// A live region: tasks appear, tick along, settle and auto-dismiss without any user action, so
// a screen reader has to be told. `aria-atomic="false"` on purpose — role="status" implies
// atomic announcements, which would re-read all three rows on every 5 s progress tick.
const Bar = styled.div.attrs({ role: 'status', 'aria-live': 'polite', 'aria-atomic': 'false' })`
  padding-bottom: ${spacing.r16};
  padding-right: ${spacing.r24};
`;

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.r8};
  max-height: calc(${VISIBLE_ROWS} * ${ROW_HEIGHT} + ${VISIBLE_ROWS - 1} * ${spacing.r8});
  overflow-y: auto;
  /* Reserve the scrollbar track so rows don't shift sideways when the 4th task arrives. */
  scrollbar-gutter: stable;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.r8};
  min-height: ${ROW_HEIGHT};
  /* Never let a row be squeezed by the capped height — overflow must scroll instead. */
  flex-shrink: 0;
`;

// One ordered row per task: description + a status badge (running/succeeded/failed).
export const BackgroundTasksBar: React.FC = () => {
  const tasks = useContext(BackgroundTasksContext);
  if (tasks.length === 0) return null;
  return (
    <Bar>
      <TaskList role="list" aria-label="Background tasks">
        {tasks.map((t) => (
          <Row key={t.taskId} role="listitem">
            <Box display="flex" alignItems="center" gap={spacing.r8}>
              {t.status === 'running' && <Loader size="smaller" />}
              {/* While running, show the tool's live progress line (carries the % once it moves);
                  until the first progress poll lands, just the task name. */}
              <BasicText>{t.status === 'running' && t.statusMessage ? t.statusMessage : t.description}</BasicText>
            </Box>
            <TextBadge variant={BADGE_VARIANT[t.status]} text={LABEL[t.status]} />
          </Row>
        ))}
      </TaskList>
    </Bar>
  );
};
